import { useMemo } from "react";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";

import {
  NOT_RELEASED_EXECUTION,
  UNKNOWN_EXECUTION,
  toPickExecutions,
  toPickLine,
} from "./wire";
import type { QueryLike } from "./async";
import type { PickExecution, Task, TaskDetail } from "./types";

/**
 * The task detail drawer's reads.
 *
 * The `Task` is not fetched here - it is already in the caller's hand, because
 * a drawer only ever opens from a row that is already on screen (a plan card's
 * task row or the all-tasks table), the same reason `usePlanTasks` is handed
 * `routeCodes` rather than reading them again. What this hook actually reads is
 * the two things a row does not carry: the task's pick lines, and what has
 * happened to each of them.
 *
 * Two endpoints because the service keeps the plan for a line and the record of
 * what was done to it in different places, and neither is derivable from the
 * other - `GetTaskDetails` for the lines, `ListTaskHistory` for the execution
 * state joined onto them by `pick_id` (see `toPickExecutions` in `wire.ts`).
 *
 * Not the requirements each pick has to prove, though - that is the engine
 * room's question and the task plan detail page's answer (see the `TaskDetail`
 * docblock in `types.ts`).
 */

/**
 * The path params both of the drawer's reads take.
 *
 * Named apart from the calls below so the drawer's address lives in one place -
 * `taskId` is the warehouse-scoped number and `id` the plan's ULID, a pairing
 * worth stating once rather than at every call site. The two endpoints take the
 * same three, which is why one helper serves both.
 */
export function taskDetailPathInit(
  orgId: string,
  planId: string,
  taskId: number,
) {
  return { params: { path: { orgId, id: planId, taskId } } };
}

/**
 * The task detail drawer's data, for one already-known task and plan.
 *
 * `orgId: null` (resolving) leaves the read disabled - see `useTaskPlans` for
 * why that gate lives here rather than in the route.
 */
export function useTaskDetail({
  orgId,
  planId,
  taskId,
  task,
}: {
  orgId: string | null;
  /** The task plan's ULID - `GetTaskDetails`'s `id`. */
  planId: string;
  /** The warehouse-scoped task number `GetTaskDetails` calls `taskId`. */
  taskId: number;
  /**
   * The row that was clicked.
   *
   * Not the plan it belongs to as well: the All tasks tab lists task rows and
   * never holds a `TaskPlan` at all, so requiring one would have made this hook
   * callable from only one of the two tabs that open the drawer. Everything the
   * drawer names the plan by (`planId`, `planName`) already rides on the task -
   * see `toTask` in `wire.ts`, which fills both from whichever listing the row
   * came from.
   */
  task: Task;
}): QueryLike<TaskDetail> {
  const client = useMemo(() => apiClient(), []);
  const ready = typeof orgId === "string";
  const pathInit = taskDetailPathInit(orgId ?? "", planId, taskId);

  const picks = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks/{taskId}/details",
    pathInit,
    { enabled: ready, select: (data) => data.task_detail_picks },
  );

  /**
   * A planned task's picks have no execution to report - `/history` reads
   * `task_activation`, and a task nobody released has no row there at all
   * (§0) - so the read is disabled rather than made and answered with
   * nothing. The fallback each line takes below becomes kind-dependent for
   * the same reason: `UNKNOWN_EXECUTION` would tell a manager the request
   * failed or came back empty, when the honest answer is that there was no
   * request to make.
   */
  const history = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks/{taskId}/history",
    pathInit,
    { enabled: ready && task.kind === "activated", select: toPickExecutions },
  );

  const missing: PickExecution =
    task.kind === "planned" ? NOT_RELEASED_EXECUTION : UNKNOWN_EXECUTION;

  /**
   * The join, memoised on the two `data` identities React Query is already
   * keeping stable across renders - so a drawer that re-renders for any other
   * reason does not hand its table a fresh array of fresh rows.
   */
  const lines = useMemo(
    () =>
      (picks.data ?? []).map((pick) =>
        toPickLine(pick, history.data?.get(pick.id) ?? missing),
      ),
    [picks.data, history.data, missing],
  );

  /**
   * Whether the history read is still worth waiting on.
   *
   * Waited on at all, so the Status column does not draw a full table of
   * unknowns for however long the read has left to run and then replace every
   * one of them - a flash of "we could not find out" over a request that was
   * still in flight.
   *
   * But only until it first fails. `isPending` stays true across React Query's
   * retries, so gating on it alone holds the whole drawer on a loader through
   * the retry backoff of a read that has already answered - which is exactly
   * backwards, since the point of drawing `unknown` per line rather than
   * failing the drawer is that the pick lines do not wait on the history. The
   * retries still run and still fill the column in if one of them lands; what
   * `failureCount` buys is that the table stops waiting on them.
   *
   * **The trap**: `enabled: false` makes react-query report `isPending: true`
   * forever (`fetchStatus: "idle"` - it is not pending in the "in flight"
   * sense, it is pending in the sense of "no result exists yet", and a
   * disabled query never asks for one). Without the `task.kind === "activated"`
   * guard, a planned task's *disabled* history query would hold
   * `historyUnanswered` true forever, and the drawer would sit on its loader
   * for every planned task and never render a single row. That is not a
   * cosmetic wrong answer; it is a total failure of this drawer for half the
   * tasks on this tab.
   */
  const historyUnanswered =
    task.kind === "activated" &&
    history.isPending &&
    history.failureCount === 0;

  if (picks.isPending || historyUnanswered) {
    return { isPending: true, isError: false, error: null, data: undefined };
  }

  if (picks.isError) {
    return {
      isPending: false,
      isError: true,
      error: picks.error,
      data: undefined,
    };
  }

  // A failed history read is deliberately not a failed drawer. The pick lines
  // are what the drawer is for and they arrived; the join above has already
  // turned the missing half into `unknown` per line, which the Status column
  // draws as such rather than as a state anybody reported.
  return {
    isPending: false,
    isError: false,
    error: null,
    data: { task, picks: lines },
  };
}
